import { LISTING_STATE_PUBLISHED } from '../../util/types';
import api from '../../api';
import { get as internalGet } from '../../util/api';
import { parseListingToSharetribe } from '../../util/sharetribeParser';
import { scoreAndFilterListings } from '../../util/listingScoring';

/**
 * Create a slim listing object for SSR to reduce HTML payload size.
 * Keeps only fields needed for:
 * - Scoring logic (discipline, rating, images count, description length, region, etc.)
 * - URL generation (id, title, publicData)
 * - Basic UI display (title, discipline, location, price, first image)
 */
const createSlimListingForSSR = (listing) => {
  const { id, attributes, images, author } = listing;
  const { title, price, publicData, deleted, createdAt } = attributes || {};
  const { discipline, groupType, duration, activity, multiLangAddressParts, type, advancedAvailabilityPlan } = publicData || {};
  
  return {
    id,
    attributes: {
      title,
      price,
      deleted,
      createdAt,
      // Keep description length for scoring but not full text
      descriptionLength: attributes?.description?.length || 0,
      publicData: {
        discipline,
        groupType,
        duration,
        activity,
        multiLangAddressParts,
        type,
        advancedAvailabilityPlan,
        // Keep region/city for scoring
        region: publicData?.region,
        city: publicData?.city,
      },
    },
    // Keep only first image for SSR
    images: images?.length > 0 ? [images[0]] : [],
    // Keep author rating and displayName for scoring and URL generation
    author: {
      id: author?.id,
      profileImage: author?.profileImage,
      attributes: {
        profile: {
          displayName: author?.attributes?.profile?.displayName,
          metadata: author?.attributes?.profile?.metadata || {},
        },
      },
    },
    // Preserve images count for scoring
    imagesCount: images?.length || 0,
  };
};

// ================ Action types ================ //

export const QUERY_POPULAR_LISTINGS_REQUEST = 'app/LandingPage/QUERY_LISTINGS_REQUEST';
export const QUERY_POPULAR_LISTINGS_SUCCESS = 'app/LandingPage/QUERY_LISTINGS_SUCCESS';
export const QUERY_POPULAR_LISTINGS_ERROR = 'app/LandingPage/QUERY_LISTINGS_ERROR';

export const QUERY_ACTIVITIES_COUNT_REQUEST = 'app/LandingPage/QUERY_ACTIVITIES_COUNT_REQUEST';
export const QUERY_ACTIVITIES_COUNT_SUCCESS = 'app/LandingPage/QUERY_ACTIVITIES_COUNT_SUCCESS';
export const QUERY_ACTIVITIES_COUNT_ERROR = 'app/LandingPage/QUERY_ACTIVITIES_COUNT_ERROR';
export const QUERY_OUTDOOR_REQUEST = 'app/LandingPage/QUERY_OUTDOOR_REQUEST';
export const QUERY_OUTDOOR_SUCCESS = 'app/LandingPage/QUERY_OUTDOOR_SUCCESS';
export const QUERY_OUTDOOR_ERROR = 'app/LandingPage/QUERY_OUTDOOR_ERROR';

export const QUERY_FEATURED_PROVIDERS_REQUEST = 'app/LandingPage/QUERY_FEATURED_PROVIDERS_REQUEST';
export const QUERY_FEATURED_PROVIDERS_SUCCESS = 'app/LandingPage/QUERY_FEATURED_PROVIDERS_SUCCESS';
export const QUERY_FEATURED_PROVIDERS_ERROR   = 'app/LandingPage/QUERY_FEATURED_PROVIDERS_ERROR';

// ================ Reducer ================ //

const initialState = {
  queryPopularListingsInProgress: false,
  queryPopularListingsError: null,
  popularListings: [],
  Outdoor: [],
  queryOutdoorInProgress: false,
  queryOutdoorError: null,
  queryActivitiesCountInProgress: false,
  queryActivitiesCountError: null,
  activitiesCount: [],
  featuredProviders: [],
  queryFeaturedProvidersInProgress: false,
  queryFeaturedProvidersError: null,
};

export default function landingPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;

  switch (type) {
    case QUERY_POPULAR_LISTINGS_REQUEST:
      return {
        ...state,
        queryPopularListingsInProgress: true,
        queryPopularListingsError: null,
      };
    case QUERY_POPULAR_LISTINGS_SUCCESS:
      return {
        ...state,
        queryPopularListingsInProgress: false,
        popularListings: payload.listings,
      };
    case QUERY_POPULAR_LISTINGS_ERROR:
      return {
        ...state,
        queryPopularListingsInProgress: false,
        queryPopularListingsError: payload,
      };
    case QUERY_ACTIVITIES_COUNT_REQUEST:
      return {
        ...state,
        queryActivitiesCountInProgress: true,
        queryActivitiesCountError: null,
      };
    case QUERY_ACTIVITIES_COUNT_SUCCESS:
      return {
        ...state,
        queryActivitiesCountInProgress: false,
        activitiesCount: payload.activitiesCount,
      };
    case QUERY_ACTIVITIES_COUNT_ERROR:
      return {
        ...state,
        queryActivitiesCountInProgress: false,
        queryActivitiesCountError: payload,
      };
    case QUERY_OUTDOOR_REQUEST:
        return {
          ...state,
          queryOutdoorInProgress: true,
          queryOutdoorError: null,
       };  
    case QUERY_OUTDOOR_SUCCESS:
      return {
        ...state,
        Outdoor: payload?.data?.[0]?.Components || [],
      };
    case QUERY_OUTDOOR_ERROR:
        return {
          ...state,
          queryOutdoorInProgress: false,
          queryOutdoorError: payload,
        };
    case QUERY_FEATURED_PROVIDERS_REQUEST:
      return { ...state, queryFeaturedProvidersInProgress: true, queryFeaturedProvidersError: null };
    case QUERY_FEATURED_PROVIDERS_SUCCESS:
      return { ...state, queryFeaturedProvidersInProgress: false, featuredProviders: payload.providers };
    case QUERY_FEATURED_PROVIDERS_ERROR:
      return { ...state, queryFeaturedProvidersInProgress: false, queryFeaturedProvidersError: payload };
    default:
      return state;
  }
}

// ================ Action creators ================ //

export const queryPopularListingsRequest = () => ({
  type: QUERY_POPULAR_LISTINGS_REQUEST,
});

export const queryPopularListingsSuccess = listings => ({
  type: QUERY_POPULAR_LISTINGS_SUCCESS,
  payload: { listings },
});

export const queryPopularListingsError = e => ({
  type: QUERY_POPULAR_LISTINGS_ERROR,
  error: true,
  payload: e,
});

export const queryActivitiesCountRequest = () => ({
  type: QUERY_ACTIVITIES_COUNT_REQUEST,
});

export const queryActivitiesCountSuccess = activitiesCount => ({
  type: QUERY_ACTIVITIES_COUNT_SUCCESS,
  payload: { activitiesCount },
});

export const queryActivitiesCountError = e => ({
  type: QUERY_ACTIVITIES_COUNT_ERROR,
  error: true,
  payload: e,
});


export const outdoorRequest = () => ({
  type: QUERY_OUTDOOR_REQUEST,
});

export const outdoorSuccess = response => ({
  type: QUERY_OUTDOOR_SUCCESS,
  payload: response,
});

export const outdoorError = e => ({
  type: QUERY_OUTDOOR_ERROR,
  error: true,
  payload: e,
});

export const queryFeaturedProvidersRequest = () => ({ type: QUERY_FEATURED_PROVIDERS_REQUEST });
export const queryFeaturedProvidersSuccess = providers => ({
  type: QUERY_FEATURED_PROVIDERS_SUCCESS,
  payload: { providers },
});
export const queryFeaturedProvidersError = e => ({
  type: QUERY_FEATURED_PROVIDERS_ERROR,
  error: true,
  payload: e,
});

// ================ Thunks ================ //

export const queryPopularListings = () => (dispatch, getState, sdk) => {
  dispatch(queryPopularListingsRequest());

  const { partner } = getState().ui;

  const publishedListings = { 'attributes.state': { $eq: LISTING_STATE_PUBLISHED } };
  const notDeleted = { 'attributes.deleted': { $ne: true } };
  const mostRecent = { $sort: '-attributes.createdAt' };
  // Fetch more listings to have a larger pool for scoring and filtering
  const limit = { $limit: 200 }; 

  const newParams = {
    ...publishedListings,
    ...notDeleted,
    ...mostRecent,
    ...limit,
  };

  console.log('🔍 Landing Page: Fetching popular listings with params:', newParams);

  return api.listings
    .fetchListings(newParams, partner?.key)
    .then(res => {
      const { items } = res.data;
      console.log('✅ Landing Page: Fetched', items.length, 'listings');
      const listings = items.map(listing => parseListingToSharetribe(listing, listing.author));

      // Apply scoring system to get diverse, high-quality listings
      console.log('📊 Landing Page: Applying scoring and diversity filters...');
      const scoredAndFilteredListings = scoreAndFilterListings(listings, {
        limit: 48, // Return top 48 diverse listings for carousel
      });

      console.log(
        '✨ Landing Page: Selected',
        scoredAndFilteredListings.length,
        'listings after scoring and diversity filtering'
      );

      // Log sample of scoring details in development
      if (process.env.NODE_ENV === 'development' && scoredAndFilteredListings.length > 0) {
        console.log('📊 Top scoring listing:', {
          title: scoredAndFilteredListings[0]?.attributes?.title,
          score: scoredAndFilteredListings[0]?.score?.toFixed(1),
          discipline: scoredAndFilteredListings[0]?.attributes?.publicData?.discipline,
          region:
            scoredAndFilteredListings[0]?.attributes?.publicData?.region ||
            scoredAndFilteredListings[0]?.attributes?.publicData?.multiLangAddressParts?.english
              ?.region,
        });
      }

      // For SSR, create slim listings to reduce HTML payload
      const isSSR = typeof window === 'undefined';
      const listingsToStore = isSSR
        ? scoredAndFilteredListings.map(createSlimListingForSSR)
        : scoredAndFilteredListings;

      dispatch(queryPopularListingsSuccess(listingsToStore));
      return scoredAndFilteredListings;
    })
    .catch(e => {
      console.error('❌ Landing Page: Failed to fetch listings:', e);
      dispatch(queryPopularListingsError(e));
      throw e;
    });
};

export const queryActivitiesCount = () => (dispatch, getState, sdk) => {
  dispatch(queryActivitiesCountRequest());

  return api.listings
    .fetchActivitiesCount()
    .then(res => {
      const { data } = res;

      const activitiesCount = data.map(activity => ({
        activity: activity._id,
        count: activity.count,
      }));

      dispatch(queryActivitiesCountSuccess(activitiesCount));
    })
    .catch(e => {
      dispatch(queryActivitiesCountError(e));
      throw e;
    });
};

export const queryFeaturedProviders = () => dispatch => {
  dispatch(queryFeaturedProvidersRequest());
  return internalGet('/api/cached-listings/providers')
    .then(data => {
      dispatch(queryFeaturedProvidersSuccess(data.providers || []));
    })
    .catch(e => {
      dispatch(queryFeaturedProvidersError(e));
    });
};

export const loadData = context => async dispatch => {
  const isSSR = typeof window === 'undefined';
  
  // SSR: Use pre-scored listings from unified cache (updated daily at 2 AM)
  if (isSSR) {
    try {
      // Access globally available cache (set by server at startup)
      if (global.listingsCache) {
        const cachedListings = global.listingsCache.getScoredListings();
        
        if (cachedListings && cachedListings.length > 0) {
          console.log(`✅ Landing Page SSR: Using ${cachedListings.length} pre-scored listings from cache`);
          dispatch(queryPopularListingsSuccess(cachedListings));

          // Also dispatch featured providers from the same cache
          const featuredProviders = global.listingsCache.getFeaturedProviders(12);
          if (featuredProviders && featuredProviders.length > 0) {
            console.log(`✅ Landing Page SSR: ${featuredProviders.length} featured providers from cache`);
            dispatch(queryFeaturedProvidersSuccess(featuredProviders));
          }

          return cachedListings;
        }
      }
      
      console.warn('⚠️ Landing Page SSR: Cache not available, falling back to live fetch');
    } catch (error) {
      console.error('❌ Landing Page SSR: Cache error, falling back to live fetch:', error.message);
    }
    
    // Fallback: fetch and score live (slower)
    return dispatch(queryPopularListings());
  }
  
  // Client-side: Check Redux state first
  const state = context?.store?.getState?.();
  const existingListings = state?.LandingPage?.popularListings;
  const existingProviders = state?.LandingPage?.featuredProviders;

  const listingsPromise = (!existingListings || existingListings.length === 0)
    ? dispatch(queryPopularListings())
    : Promise.resolve(existingListings);

  const providersPromise = (!existingProviders || existingProviders.length === 0)
    ? dispatch(queryFeaturedProviders())
    : Promise.resolve(existingProviders);

  return Promise.all([listingsPromise, providersPromise]);
};
